// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, { useEffect, useState } from 'react';
import { Row, Col } from 'antd/lib/grid';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';
import InputNumber from 'antd/lib/input-number';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import Switch from 'antd/lib/switch';
import Tag from 'antd/lib/tag';
import notification from 'antd/lib/notification';
import { ArrowRightOutlined } from '@ant-design/icons';

import CVATTooltip from 'components/common/cvat-tooltip';
import { clamp } from 'utils/math';
import {
    MLModel, ModelKind, ModelReturnType, DimensionType, Label,
} from 'cvat-core-wrapper';

import LabelsMapperComponent, { LabelInterface, FullMapping } from './labels-mapper';

interface Props {
    withCleanup: boolean;
    models: MLModel[];
    labels: Label[];
    dimension: DimensionType;
    runInference(model: MLModel, body: object): void;
}

type ServerMapping = Record<string, {
    name: string;
    attributes: Record<string, string>;
    sublabels?: ServerMapping;
}>;

export interface DetectorRequestBody {
    mapping: ServerMapping;
    cleanup: boolean;
    convMaskToPoly: boolean;
    // Optional for prompt-based models
    prompt?: string;
    label?: string;
    threshold?: number | {
        thr: number;
        prompt: string;
        label: string;
        output_type: 'polygon' | 'mask' | 'rectangle';
    };
    output_type?: 'polygon' | 'mask' | 'rectangle';
}

function convertMappingToServer(mapping: FullMapping): ServerMapping {
    return mapping.reduce<ServerMapping>((acc, [modelLabel, taskLabel, attributesMapping, subMapping]) => (
        {
            ...acc,
            [modelLabel.name]: {
                name: taskLabel.name,
                attributes: attributesMapping.reduce<Record<string, string>>((attrAcc, val) => {
                    if (val[0]?.name && val[1]?.name) {
                        attrAcc[val[0].name] = val[1].name;
                    }
                    return attrAcc;
                }, {}),
                ...(subMapping.length ? { sublabels: convertMappingToServer(subMapping) } : {}),
            },
        }
    ), {});
}

function DetectorRunner(props: Props): JSX.Element {
    const {
        models, withCleanup, labels, dimension, runInference,
    } = props;

    const [modelID, setModelID] = useState<string | null>(null);
    const [threshold, setThreshold] = useState<number>(0.5);
    const [distance, setDistance] = useState<number>(50);
    const [cleanup, setCleanup] = useState<boolean>(false);
    const [mapping, setMapping] = useState<FullMapping>([]);
    const [convertMasksToPolygons, setConvertMasksToPolygons] = useState<boolean>(false);
    const [modelLabels, setModelLabels] = useState<LabelInterface[]>([]);
    const [taskLabels, setTaskLabels] = useState<LabelInterface[]>([]);

    const [prompt, setPrompt] = useState<string>('');
    const [targetLabel, setTargetLabel] = useState<string>('');
    const [promptThreshold, setPromptThreshold] = useState<number>(0.25);
    const [outputType, setOutputType] = useState<'polygon' | 'mask' | 'rectangle'>('polygon');

    const model = models.find((_model): boolean => _model.id === modelID);
    const isDetector = model?.kind === ModelKind.DETECTOR;
    const isReId = model?.kind === ModelKind.REID;

    const supportsTextPrompt = Boolean(
        isDetector && model && typeof (model as any).name === 'string' &&
        ((model as any).name as string).toLowerCase().includes('prompt'),
    );

    const convertMasks2PolygonVisible = isDetector &&
        (!model.returnType || model.returnType === ModelReturnType.MASK);

    const buttonEnabled = Boolean(model && (
        isReId || (
            isDetector && (
                supportsTextPrompt ? (prompt.trim().length > 0 && !!targetLabel) : mapping.length
            )
        )
    ));

    useEffect(() => {
        // Initialize defaults for prompt UI
        if (supportsTextPrompt) {
            if (!targetLabel && labels.length) {
                setTargetLabel(labels[0].name);
            }
        } else {
            // reset prompt fields when switching away
            setPrompt('');
            setTargetLabel('');
            setPromptThreshold(0.25);
            setOutputType('polygon');
        }
    }, [supportsTextPrompt, modelID]);

    useEffect(() => {
        const converted = labels.map((label) => ({
            name: label.name,
            type: label.type,
            color: label.color,
            attributes: label.attributes.map((attr) => ({
                name: attr.name,
                input_type: attr.inputType,
                values: [...attr.values],
            })),
            sublabels: (label.structure?.sublabels || []).map((sublabel) => ({
                name: sublabel.name,
                type: sublabel.type,
                color: sublabel.color,
                attributes: sublabel.attributes.map((attr) => ({
                    name: attr.name,
                    input_type: attr.inputType,
                    values: [...attr.values],
                })),
            })),
        }));

        setTaskLabels(converted);
        if (model) {
            setModelLabels(model.labels);
            if (!model.labels.length && model.kind !== ModelKind.REID) {
                notification.warning({ message: 'This model does not have specified labels' });
            }
        } else {
            setModelLabels([]);
        }
    }, [labels, model]);

    return (
        <div className='cvat-run-model-content'>
            <Row align='middle'>
                <Col span={4}>Model:</Col>
                <Col span={20}>
                    <Select
                        placeholder={dimension === DimensionType.DIMENSION_2D ? 'Select a model' : 'No models available'}
                        disabled={dimension !== DimensionType.DIMENSION_2D}
                        style={{ width: '100%' }}
                        onChange={(_modelID: string): void => {
                            setModelID(_modelID);
                        }}
                    >
                        {models.map(
                            (_model: MLModel): JSX.Element => (
                                <Select.Option value={_model.id} key={_model.id}>
                                    {_model.name}
                                </Select.Option>
                            ),
                        )}
                    </Select>
                </Col>
            </Row>
            {isDetector && supportsTextPrompt && (
                <div className='cvat-detector-runner-prompt-wrapper'>
                    <Row align='middle'>
                        <Col span={6}><Text>Target label</Text></Col>
                        <Col span={18}>
                            <Select style={{ width: '100%' }}
                                    placeholder='Select target label'
                                    value={targetLabel || undefined}
                                    onChange={(value: string) => setTargetLabel(value)}>
                                {labels.map((label) => (
                                    <Select.Option value={label.name} key={label.name}>
                                        {label.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                    </Row>

                    <Row align='middle'>
                        <Col span={6}><Text>Prompt</Text></Col>
                        <Col span={18}>
                            <Input.TextArea
                                autoSize={{ minRows: 2, maxRows: 6 }}
                                placeholder='E.g. forest, road, buildings'
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                            />
                        </Col>
                    </Row>

                    <Row align='middle'>
                        <Col span={6}><Text>Output</Text></Col>
                        <Col span={18}>
                            <Select style={{ width: '100%' }}
                                    value={outputType}
                                    onChange={(value: 'polygon' | 'mask' | 'rectangle') => setOutputType(value)}>
                                <Select.Option value='polygon'>polygon</Select.Option>
                                <Select.Option value='mask'>mask</Select.Option>
                                <Select.Option value='rectangle'>rectangle</Select.Option>
                            </Select>
                        </Col>
                    </Row>

                    <Row align='middle'>
                        <Col span={6}><Text>Threshold</Text></Col>
                        <Col span={18}>
                            <InputNumber min={0} max={1} step={0.01}
                                         value={promptThreshold}
                                         onChange={(value) => {
                                             if (value !== null && value !== undefined) {
                                                 setPromptThreshold(clamp(+value, 0, 1));
                                             }
                                         }}
                            />
                        </Col>
                    </Row>
                </div>
            )}
            {isDetector && !supportsTextPrompt && (
                <div>
                    <div className='cvat-detector-runner-mapping-header'>
                        <div>
                            <Text strong>Setup mapping between labels and attributes</Text>
                        </div>
                        <div>
                            <Tag>Model Spec</Tag>
                            <ArrowRightOutlined />
                            <Tag>CVAT Spec</Tag>
                        </div>
                    </div>
                    <LabelsMapperComponent
                        key={modelID} // rerender when model switched
                        onUpdateMapping={(_mapping: FullMapping) => setMapping(_mapping)}
                        modelLabels={modelLabels}
                        taskLabels={taskLabels}
                    />
                </div>
            )}
            {convertMasks2PolygonVisible && (
                <div className='cvat-detector-runner-convert-masks-to-polygons-wrapper'>
                    <Switch
                        checked={convertMasksToPolygons}
                        onChange={(checked: boolean) => {
                            setConvertMasksToPolygons(checked);
                        }}
                    />
                    <Text>Convert masks to polygons</Text>
                </div>
            )}
            {isDetector && withCleanup && (
                <div className='cvat-detector-runner-clean-previous-annotations-wrapper'>
                    <Switch
                        checked={cleanup}
                        onChange={(checked: boolean): void => setCleanup(checked)}
                    />
                    <Text>Clean previous annotations</Text>
                </div>
            )}
            {isReId ? (
                <div>
                    <Row align='middle' justify='start'>
                        <Col>
                            <Text>Threshold</Text>
                        </Col>
                        <Col offset={1}>
                            <CVATTooltip title='Minimum similarity value for shapes that can be merged'>
                                <InputNumber
                                    min={0.01}
                                    step={0.01}
                                    max={1}
                                    value={threshold}
                                    onChange={(value: number | undefined | string | null) => {
                                        if (typeof value !== 'undefined' && value !== null) {
                                            setThreshold(clamp(+value, 0.01, 1));
                                        }
                                    }}
                                />
                            </CVATTooltip>
                        </Col>
                    </Row>
                    <Row align='middle' justify='start'>
                        <Col>
                            <Text>Maximum distance</Text>
                        </Col>
                        <Col offset={1}>
                            <CVATTooltip title='Maximum distance between shapes that can be merged'>
                                <InputNumber
                                    placeholder='Threshold'
                                    min={1}
                                    value={distance}
                                    onChange={(value: number | undefined | string | null) => {
                                        if (typeof value !== 'undefined' && value !== null) {
                                            setDistance(+value);
                                        }
                                    }}
                                />
                            </CVATTooltip>
                        </Col>
                    </Row>
                </div>
            ) : null}
            <Row align='middle' justify='end'>
                <Col>
                    <Button
                        className='cvat-inference-run-button'
                        disabled={!buttonEnabled}
                        type='primary'
                        onClick={() => {
                            if (!model) return;

                            if (model.kind === ModelKind.DETECTOR) {
                                if (supportsTextPrompt) {
                                    const serverMapping: ServerMapping = {
                                        [targetLabel]: { name: targetLabel, attributes: {} },
                                    };
                                    const packedThreshold = {
                                        thr: promptThreshold,
                                        prompt: prompt.trim(),
                                        label: targetLabel,
                                        output_type: outputType,
                                    };
                                    runInference(model, {
                                        mapping: serverMapping,
                                        cleanup,
                                        convMaskToPoly: convertMasksToPolygons,
                                        threshold: packedThreshold,
                                    });
                                } else {
                                    const serverMapping = convertMappingToServer(mapping);
                                    runInference(model, { mapping: serverMapping, cleanup, convMaskToPoly: convertMasksToPolygons });
                                }
                            } else if (model.kind === ModelKind.REID) {
                                runInference(model, { threshold, max_distance: distance });
                            }
                        }}
                    >
                        Annotate
                    </Button>
                </Col>
            </Row>
        </div>
    );
}

export default React.memo(DetectorRunner);
